import { TableRowSelection } from 'antd/es/table/interface'
import _ from 'lodash'
import React, { useCallback, useMemo } from 'react'
import { GeckoEntry } from 'types'

/**
 * A hook to build the row selection prop for the table.
 *
 */
export function useRowSelection<T>(
  entries: readonly GeckoEntry[],
  groupByType: string,
  selectedRowKeys: ReadonlyArray<string>,
  setSelectedRowKeys?: (keys: ReadonlyArray<string>) => void
): TableRowSelection<T> {
  // NB: We store the panel state as an array of strings for serializability
  // But the antd table requires an array of React.Key to facilitate selection.
  // So we convert between them.
  const selectedRowKeysAsKeys = useMemo(
    () => selectedRowKeys.map((key): React.Key => key),
    [selectedRowKeys]
  )

  const setSelectedRowKeyAdapter = useCallback(
    (keys: React.Key[]) => {
      const newKeys = keys.map((key) => key.toString())
      setSelectedRowKeys?.(newKeys)
    },
    [setSelectedRowKeys]
  )

  const onSelectChange = useOnSelectChange(
    entries,
    groupByType,
    selectedRowKeysAsKeys,
    setSelectedRowKeyAdapter
  )

  const rowSelection: TableRowSelection<T> = useMemo(
    () => ({
      selectedRowKeys: selectedRowKeysAsKeys,
      onChange: onSelectChange,
      defaultSelectedRowKeys: selectedRowKeysAsKeys,
    }),
    [onSelectChange, selectedRowKeysAsKeys]
  )

  return rowSelection
}

/**
 * The antd table will deselect selections outside the current page when making a selection
 * We don't want this, so we:
 * Find keys being removed (diff selected keys and previously-selected keys
 * If they aren't in the current entires, then add them back to the selected set
 *
 */

export function useOnSelectChange(
  entries: readonly GeckoEntry[],
  groupByType: string,
  selectedRowKeys: React.Key[],
  setSelectedRowKeys: (keys: React.Key[]) => void
) {
  return useCallback(
    (newSelectedRowKeys: React.Key[]) => {
      const entryIdsOnCurrentPage = entries
        .filter((e) => e.type === groupByType)
        .map((e) => e.id)

      const keysBeingRemoved = _.difference(selectedRowKeys, newSelectedRowKeys)

      const keysBeingRemovedToAddBack = keysBeingRemoved.filter(
        (key) => !entryIdsOnCurrentPage.includes(key.toString())
      )

      setSelectedRowKeys([...newSelectedRowKeys, ...keysBeingRemovedToAddBack])
    },
    [entries, groupByType, selectedRowKeys, setSelectedRowKeys]
  )
}
